import { FC, useState } from 'react';
import { sumBy } from 'lodash';
import Typography from '@mui/material/Typography';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { styled } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Row } from 'further-ui/components/Layout';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import { Transaction } from './types';
import getColumns from './getColumns';
import Subtransactions from './Subtransactions';
import AccountingTable from '../components/AccountingTable';

const StyledAccordion = styled(Accordion, {
  shouldForwardProp: (prop) => prop !== 'isLast',
})<{ isLast: boolean }>(({ theme, isLast }) => ({
  borderBottom: isLast ? 'none' : `1px solid ${theme.palette.grey[300]}`,
  margin: '0 !important',
  overflow: 'visible',
  '&::before': {
    display: 'none',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  margin: 0,
  minHeight: '56px !important',
  '& .MuiAccordionSummary-content': {
    margin: 0,
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    marginLeft: theme.spacing(theme.spacingSizes.sm),
    '& .MuiSvgIcon-root': {
      color: theme.palette.grey[600],
    },
  },
  '&.Mui-expanded .MuiAccordionSummary-content': {
    margin: 0,
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)({
  padding: 0,
});

const getExpandedContent =
  (baseCurrency: CurrencyCode) =>
  ({ subtransactions }: Transaction) =>
    (
      <Subtransactions
        subtransactions={subtransactions ?? []}
        baseCurrency={baseCurrency}
      />
    );

type Props = {
  accountLabel: string;
  isLoading: boolean;
  transactions: Array<Transaction>;
  baseCurrency: CurrencyCode;
  isLast: boolean;
};

const Account: FC<Props> = ({
  accountLabel,
  transactions,
  isLoading,
  baseCurrency,
  isLast,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { expandedRows, toggleRowExpansion } = useExpandedRows();

  const columns = getColumns({ baseCurrency });
  const totalAmount = sumBy(
    transactions,
    (transaction) => transaction.amountIncVatLcy ?? 0,
  );
  const totalAmountFormatted = numberToCurrencyString(totalAmount, {
    currency: baseCurrency,
    negativeInBrackets: true,
  });

  return (
    <StyledAccordion expanded={isExpanded} isLast={isLast}>
      <StyledAccordionSummary
        expandIcon={
          !isExpanded ? (
            <ExpandMoreIcon fontSize="small" color="primary" />
          ) : null
        }
        aria-controls={`general-ledger-account-${accountLabel}`}
        id={`general-ledger-account-${accountLabel}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Row fullWidth centered justify="space-between">
          <Typography variant="h6">{accountLabel}</Typography>
          {!isExpanded ? (
            <Typography variant="h6">{totalAmountFormatted}</Typography>
          ) : null}
        </Row>
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        <AccountingTable
          columns={columns}
          rows={transactions}
          isLoading={isLoading}
          isExpandableRow={({ subtransactions }) =>
            !!subtransactions && subtransactions.length > 0
          }
          isExpandedRow={({ id }) => expandedRows.has(id)}
          onRowExpand={({ id }) => toggleRowExpansion(id)}
          onRowClick={({ id }) => toggleRowExpansion(id)}
          getExpandedContent={getExpandedContent(baseCurrency)}
          stickyExpandedContent={false}
          borderless
          externalScroll
          customFooterRow={[
            {
              key: 'total',
              content: (
                <Row centered spacing="xs">
                  <Typography variant="h6">Subtotal</Typography>
                  <Typography variant="body2">{accountLabel}</Typography>
                </Row>
              ),
              colspan: 5,
              backgroundColor: grey[50],
            },
            {
              key: 'totalAmount',
              content: totalAmountFormatted,
              colspan: 6,
              backgroundColor: grey[50],
            },
          ]}
        />
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

export default Account;
